import { observer } from 'mobx-react-lite';
import setCardDetailsOnItem from '../actions/setCardDetailsOnItem';
import setShowBodyWithMessageCard from '../actions/setShowBodyWithMessageCard';
import showCardLoading from '../actions/showCardLoading';
import '../mutators/showCardLoadingMutator';
import fixAttachedInlineImagesForMessageCard from '../utils/fixAttachedInlineImagesForMessageCard';
import getActionableMessageCardMessageContextFromItem from '../utils/getActionableMessageCardMessageContextFromItem';
import getCardDetails from '../utils/getCardDetails';
import mailActionProvider from '../utils/mailActionProvider';
import { fixExternalInlineImages } from 'owa-actionable-message-connectors';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { logUsage } from 'owa-analytics';
import ContentHandler from 'owa-controls-content-handler/lib/schema/ContentHandler';
import { OwaDate, owaDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ActionableMessageCardInItemViewState } from 'owa-mail-reading-pane-store';
import { ClientItem } from 'owa-mail-store';
import { getNonBootUserConfiguration } from 'owa-nonboot-userconfiguration-manager';
import { trace } from 'owa-trace';
import * as React from 'react';
import ActionableMessageTeachingUI, {
    ActionableMessageTeachingUIProps,
} from './ActionableMessageTeachingUI';
import {
    ActionableMessageCard,
    CardDetails,
    getCardTypeFromCardDetails,
    MessageContext,
    ActionableMessageContentHandler,
} from 'owa-actionable-message';
import {
    actionProvider as connectorsActionProvider,
    telemetryProvider,
    makeDownloadThemeRequest,
} from 'owa-actionable-message-connectors';
import {
    cardActionProvider as transactionActionProvider,
    OPayPanel,
    getDisabledTransactionCards,
} from 'owa-opay-panel';
import isConnectorSenderDisabled from '../utils/isConnectorSenderDisabled';

export interface ActionableMessageContentProps {
    item: ClientItem;
    viewState: ActionableMessageCardInItemViewState;
    darkModeContentHandler?: ContentHandler;
    undoDarkMode?: boolean;
}

export interface ActionableMessageContentState {
    shouldLoadTeachingUI: boolean;
    currentCardElement: HTMLElement;
}

const styles = require('./actionableMessageContent.scss');
const LOAD_TIMEOUT = 4000; // amount of time to wait for the card to be ready before showing the message body

export default observer(function ActionableMessageContent(props: ActionableMessageContentProps) {
    React.useEffect(() => {
        trace.info('[MessageCardDebug] ActionableMessageContent mount');
        return () => {
            trace.info('[MessageCardDebug] ActionableMessageContent unmount');
        };
    }, []);

    React.useEffect(() => {
        trace.info('[MessageCardDebug] ActionableMessageContent update');
        getCardProperties();
    });

    React.useEffect(() => {
        loadTimeoutTimer.current = setTimeout(() => {
            // The load timed out, so hide the loading spinner and show the message body
            trace.info('[MessageCardDebug] ActionableMessageContent card load timed out');
            logUsage('actionableMessageContentLoadTimeout');
            cardLoadTimedOut.current = true;
            finishLoad(true /* show message body */);
        }, LOAD_TIMEOUT);

        return () => clearTimeout(loadTimeoutTimer.current);
    }, []);

    const messageContext = React.useRef<MessageContext>(
        getActionableMessageCardMessageContextFromItem(props.item)
    );
    const loadStartTime = React.useRef<OwaDate>(owaDate());
    const fetchStartTime = React.useRef<OwaDate>();
    const fetchEndTime = React.useRef<OwaDate>();
    const elementsModifiedForDarkMode = React.useRef<HTMLElement[]>([]);
    const fetchInProcess = React.useRef<boolean>();
    const loadTimeoutTimer = React.useRef<NodeJS.Timer>();
    const cardLoadTimedOut = React.useRef<boolean>();
    const [shouldLoadTeachingUI, setShouldLoadTeachingUI] = React.useState(false);
    const [currentCardElement, setCurrentCardElement] = React.useState(null);
    const getCardProperties = async () => {
        if (fetchInProcess.current) {
            trace.info(
                '[MessageCardDebug] ActionableMessageContent getCardProperties fetch already in process. Aborting this attempt'
            );
            return;
        }
        const { item } = props;
        fetchStartTime.current = owaDate();
        const cardDetails = item.adaptiveCardDetails;
        const owaUserConfig = await getNonBootUserConfiguration();
        const shouldShowCardBasedOnOrganizationConfig: boolean =
            owaUserConfig.SmtpActionableMessagesEnabled;
        if (cardDetails?.[0] && shouldShowCardBasedOnOrganizationConfig) {
            trace.info(
                '[MessageCardDebug] ActionableMessageContent getCardProperties already had cardDetails on the item. cardId: ' +
                    cardDetails[0].cardId
            );
            fetchEndTime.current = owaDate();
            return;
        }
        const getCardDetailsDatapoint = new PerformanceDatapoint('MCActionableCardGetProperty');
        try {
            fetchInProcess.current = true;
            trace.info(
                '[MessageCardDebug] ActionableMessageContent getCardProperties fetching cardDetails from the server'
            );
            const cardDetails = await getCardDetails(item);
            if (cardDetails && cardDetails.length > 0 && shouldShowCardBasedOnOrganizationConfig) {
                getCardDetailsDatapoint.end();
                fetchEndTime.current = owaDate();
                trace.info(
                    '[MessageCardDebug] ActionableMessageContent getCardProperties got card details from the server. cardId: ' +
                        cardDetails[0].cardId
                );
                setCardDetailsOnItem(item, cardDetails);
            } else {
                const traceInfoMessage: string = shouldShowCardBasedOnOrganizationConfig
                    ? 'getCardProperties error getting card details from the server'
                    : 'smtp Actionable Messages is disabled for org config';
                trace.info('[MessageCardDebug] ActionableMessageContent ' + traceInfoMessage);
                // There was an error, so hide the loading spinner and show the message body
                finishLoad(true /* show message body */);
                // if this function is called, but we didn't actually have card data then something is wonky with the stamping
                getCardDetailsDatapoint.endWithError(DatapointStatus.ServerError);
            }
            fetchInProcess.current = false;
        } catch (error) {
            trace.info(
                '[MessageCardDebug] ActionableMessageContent getCardProperties error getting card details from the server'
            );
            trace.warn(
                '[ActionableMessageContent] ActionableMessageContent getCardProperties: exception during getCardDetails: ' +
                    error.message
            );
            // There was an error, so hide the loading spinner and show the message body
            finishLoad(true /* show message body */);
            getCardDetailsDatapoint.endWithError(DatapointStatus.ClientError);
            fetchInProcess.current = false;
        }
    };
    const renderCard = (cardDetails): JSX.Element => {
        return (
            !cardLoadTimedOut.current && (
                <>
                    <div className={styles.cardArea}>{renderCardFromDetails(cardDetails)}</div>
                    <OPayPanel
                        odataItemId={messageContext.current.odataItemId}
                        smtpAddressesSerialized={cardDetails[0].smtpAddressesSerialized}
                    />
                    {shouldLoadTeachingUI &&
                        renderTeachingUI(cardDetails ? initilizeLearningUI() : null)}
                </>
            )
        );
    };
    // Helper method for filtering non-contextual cards
    const filterNonContextualCards = (cardDetails: CardDetails[]): CardDetails[] => {
        return cardDetails.filter(card => {
            return !card.isContextual;
        });
    };
    const undoDarkModeHandler = () => {
        const { darkModeContentHandler, undoDarkMode } = props;
        if (darkModeContentHandler && undoDarkMode) {
            trace.info(
                'ActionableMessageContent: applyDarkModeHandlerToCardElement: undoing dark mode'
            );
            darkModeContentHandler.undoHandler(elementsModifiedForDarkMode.current);
            elementsModifiedForDarkMode.current = [];
        }
    };
    const applyDarkModeHandlerToCardElement = (element: HTMLElement) => {
        const { undoDarkMode, darkModeContentHandler } = props;
        if (element && darkModeContentHandler && !undoDarkMode) {
            trace.info(
                'ActionableMessageCard: applyDarkModeHandlerToCardElement: applying dark mode to card elements'
            );
            if (darkModeContentHandler.cssSelector) {
                darkModeContentHandler.handler(element);
                elementsModifiedForDarkMode.current.push(element);
                const matchedElements = element.querySelectorAll(
                    darkModeContentHandler.cssSelector
                );
                if (matchedElements) {
                    for (let i = 0; i < matchedElements.length; i++) {
                        const matchedElement = matchedElements.item(i) as HTMLElement;
                        darkModeContentHandler.handler(matchedElement);
                        elementsModifiedForDarkMode.current.push(matchedElement);
                    }
                }
            } else {
                trace.info(
                    'ActionableMessageCard: applyDarkModeHandlerToCardElement: no cssSelector specified by DarkMode handler'
                );
            }
        }
    };
    // Function for filtering transaction cards
    const filterDisabledTransactionCards = (cardDetails: CardDetails[]): CardDetails[] => {
        let cards = filterNonContextualCards(cardDetails);
        // removes transaction cards match the filter fuction getDisabledTransactionCards
        // NOTE: we pass item, mailboxinfo, and the flight inorder to mitigate encaspulation
        // concerns and keep logging in the owa-actionable-message control package
        cards = cards.filter(card => {
            return getDisabledTransactionCards(
                card,
                props.item.MailboxInfo,
                isFeatureEnabled('rp-acDisableTransactionCard'),
                messageContext.current,
                telemetryProvider
            );
        });
        // if we the card is disabled, we MUST show the original message body, so hide the loading spinner and show the message body
        // TODO: Make this change more compatable with the concept of multiple cards in an email
        if (cards.length != 0) {
            finishLoad(true /* show message body */);
        }
        return cards;
    };
    const renderCardFromDetails = (cardDetails: CardDetails[]): JSX.Element => {
        const card = filterNonContextualCards(cardDetails);
        let actionableMessageContentHandler: ActionableMessageContentHandler = {
            onUndo: () => undoDarkModeHandler(),
            onApply: (element: HTMLElement) => applyDarkModeHandlerToCardElement(element),
        };
        if (card && card.length === 1) {
            trace.info(
                '[MessageCardDebug] ActionableMessageContent renderCardFromDetails. cardId: ' +
                    card[0].cardId
            );
            return (
                <div className={styles.container}>
                    <ActionableMessageCard
                        key={card[0].cardId}
                        cardDetails={card[0]}
                        mailboxInfo={props.item.MailboxInfo}
                        messageContext={messageContext.current}
                        showMessageBodyCallback={setShowMessageBody}
                        contentHandlerUndoMode={props.undoDarkMode}
                        actionProviders={[
                            mailActionProvider,
                            connectorsActionProvider,
                            transactionActionProvider,
                        ]}
                        telemetryProvider={telemetryProvider}
                        adjustCardElementBeforeRenderCallback={fixInlineImages}
                        onCardRendered={onCardRendered}
                        downloadThemeCallback={makeDownloadThemeRequest}
                        onCardDetailsUpdated={updateCardOnItem}
                        perfData={{
                            loadStartTime: loadStartTime.current,
                            fetchStartTime: fetchStartTime.current,
                            fetchEndTime: fetchEndTime.current,
                        }}
                        actionableMessageContentHandler={actionableMessageContentHandler}
                    />
                </div>
            );
        } else {
            trace.info('Did not find expected number of cards on the message');
            setShowMessageBody(true);
            return <div />;
        }
    };
    const setShowMessageBody = (shouldShowBody: boolean) => {
        trace.info(
            '[MessageCardDebug] ActionableMessageContent setShowMessageBody: ' + shouldShowBody
        );
        setShowBodyWithMessageCard(props.viewState, shouldShowBody);
        // If we're choosing to show the message body, then we should also stop showing the card loading spinner regardless
        // of whether the card rendered or not. We don't want to have a spinner and the message body rendered at the same time
        // because it looks really weird
        if (shouldShowBody) {
            showCardLoading(props.viewState, false);
        }
        finishLoad(shouldShowBody);
    };
    const updateCardOnItem = (cardDetails: CardDetails[]) => {
        trace.info('[MessageCardDebug] ActionableMessageContent updateCardOnItem');
        setCardDetailsOnItem(props.item, cardDetails);
    };
    const initilizeLearningUI = (): ActionableMessageTeachingUIProps => {
        const cardDetails = props.item.adaptiveCardDetails;
        if (cardDetails && cardDetails.length > 0) {
            const { messageCardContext } = cardDetails[0].card;
            if (!messageCardContext) {
                return {
                    acCorrelationId: '',
                    actionType: undefined,
                    messageId: null,
                    target: () => currentCardElement,
                };
            }
            const { correlationId } = messageCardContext;
            return {
                acCorrelationId: correlationId,
                actionType: getCardTypeFromCardDetails(cardDetails[0]),
                messageId: props.item.ItemId.Id,
                target: () => currentCardElement,
            };
        } else {
            return null;
        }
    };
    const fixInlineImages = (cardElement: HTMLElement) => {
        trace.info('[MessageCardDebug] ActionableMessageContent fixInlineImages');
        // Fix inline images for external images (i.e. hosted on a 3rd party website) by adding a call to the connectors proxy
        fixExternalInlineImages(cardElement, props.item.MailboxInfo);
        // Fix inline images for attached images (i.e. images attached to the item) by replacing the cid with a reference to a get attachment call
        fixAttachedInlineImagesForMessageCard(cardElement, props.item, props.item.MailboxInfo);
    };
    const onCardRendered = (cardElement: HTMLElement) => {
        trace.info(
            '[MessageCardDebug] ActionableMessageContent onCardRendered. Updating loading state'
        );
        if (currentCardElement !== cardElement) {
            {
                setShouldLoadTeachingUI(true);
                setCurrentCardElement(cardElement);
            }
            finishLoad(false /* show message body */);
        }
        if (props.viewState.showCardLoading) {
            // Once the card has rendered, stop showing the loading spinner
            showCardLoading(props.viewState, false);
        }
    };
    const renderTeachingUI = (props: ActionableMessageTeachingUIProps): JSX.Element => {
        if (props?.actionType && props.target != null) {
            trace.info('[ActionableMessageCard] TeachingUI triggered');
            return <ActionableMessageTeachingUI {...props} />;
        }
        return null;
    };
    const finishLoad = (forceShowMessageBody: boolean) => {
        // If there is a loading timeout timer running, clear that first
        if (loadTimeoutTimer.current) {
            clearTimeout(loadTimeoutTimer.current);
            loadTimeoutTimer.current = null;
        }
        const { viewState } = props;
        // Now hide the loading spinner and show the message body if requested
        showCardLoading(viewState, false);
        if (forceShowMessageBody) {
            setShowBodyWithMessageCard(viewState, true /*showBodyWithMessageCard*/);
        }
    };
    const cardDetails = props.item.adaptiveCardDetails;
    trace.info(
        `[MessageCardDebug] ActionableMessageContent render. itemId: ${
            props.item.ItemId.Id
        }. CardDetails: ${cardDetails?.[0] ? cardDetails[0].cardId : 'undefined'}`
    );
    const areThereCards: boolean = cardDetails && cardDetails.length > 0;
    const senderGuidDisabled: boolean =
        areThereCards && isConnectorSenderDisabled(cardDetails[0].connectorSenderGuid);
    // If there are no cards just return an empty div. Don't finish the load yet since we may still be fetching cards.
    // If there are cards, but they're disabled, then finish the load by showing the message body and return an empty div.
    if (!areThereCards) {
        return <div />;
    } else if (senderGuidDisabled || filterDisabledTransactionCards(cardDetails).length !== 0) {
        finishLoad(true /** show message body */);
        return <div />;
    }
    return renderCard(cardDetails);
});
